import { format, parseISO } from 'date-fns'
import { es } from 'date-fns/locale'
import type {
  Audiencia,
  ConexionGoogle,
  EventoGoogle,
  Expediente,
  Plazo,
  OrigenEventoGoogle,
} from '../types'

export const OPCIONES_ANTICIPACION = [
  { minutos: 10080, etiqueta: '1 semana antes' },
  { minutos: 2880, etiqueta: '2 días antes' },
  { minutos: 1440, etiqueta: '1 día antes' },
  { minutos: 120, etiqueta: '2 horas antes' },
  { minutos: 30, etiqueta: '30 minutos antes' },
]

export function etiquetaAnticipacion(minutos: number): string {
  return (
    OPCIONES_ANTICIPACION.find((o) => o.minutos === minutos)?.etiqueta ?? `${minutos} minutos antes`
  )
}

export function ahoraISO(): string {
  return new Date().toISOString()
}

export function fechaHora(iso?: string): string {
  if (!iso) return 'Nunca'
  return format(parseISO(iso), "d 'de' LLLL, HH:mm 'h'", { locale: es })
}

export function claveEvento(origen: OrigenEventoGoogle, origenId: string): string {
  return `${origen}:${origenId}`
}

export function ligaExpediente(expediente: Expediente): string {
  return `https://limaamaro.app/expedientes/${expediente.id}`
}

/** Google solo recibe lo que el abogado autorizó para ese tipo de evento. */
export function conexionAcepta(conexion: ConexionGoogle, origen: OrigenEventoGoogle, tipo?: string) {
  if (conexion.estado !== 'conectado') return false
  if (origen === 'plazo') return conexion.sincronizarVencimientos
  return tipo === 'Cita' ? conexion.sincronizarCitas : conexion.sincronizarAudiencias
}

export function eventoDeAudiencia(
  audiencia: Audiencia,
  expediente: Expediente,
  conexion: ConexionGoogle,
): EventoGoogle {
  return {
    id: `gc-${audiencia.id}`,
    origen: 'audiencia',
    origenId: audiencia.id,
    abogadoId: audiencia.abogadoId,
    calendarioId: conexion.calendarioDestino,
    titulo: `${audiencia.titulo} – Exp. ${expediente.localizador}`,
    fecha: audiencia.fecha,
    hora: audiencia.hora,
    lugar: `${audiencia.lugar} · ${audiencia.autoridad}`,
    descripcion: `${audiencia.tipo} del expediente ${expediente.localizador} (${expediente.materia}). Cliente: ${expediente.cliente}. Autoridad: ${audiencia.autoridad}.`,
    enlace: ligaExpediente(expediente),
    recordatorioMinutos: conexion.anticipacionMinutos,
    ultimaAccion: 'creado',
    actualizadoEn: ahoraISO(),
  }
}

export function eventoDePlazo(
  plazo: Plazo,
  expediente: Expediente,
  conexion: ConexionGoogle,
): EventoGoogle {
  return {
    id: `gc-${plazo.id}`,
    origen: 'plazo',
    origenId: plazo.id,
    abogadoId: plazo.responsableId,
    calendarioId: conexion.calendarioDestino,
    titulo: `Vence: ${plazo.concepto} – Exp. ${expediente.localizador}`,
    fecha: plazo.fechaLimite,
    lugar: expediente.autoridad,
    descripcion: `${plazo.tipo} del expediente ${expediente.localizador} (${expediente.materia}). Cliente: ${expediente.cliente}.`,
    enlace: ligaExpediente(expediente),
    recordatorioMinutos: conexion.anticipacionMinutos,
    ultimaAccion: 'creado',
    actualizadoEn: ahoraISO(),
  }
}

/**
 * Recalcula el espejo completo del sistema hacia Google: crea lo que falta,
 * actualiza lo que cambió y elimina lo que ya no debe estar en el calendario.
 */
export function recalcularEventos(
  conexiones: ConexionGoogle[],
  audiencias: Audiencia[],
  plazos: Plazo[],
  expedientes: Expediente[],
  previos: EventoGoogle[],
): EventoGoogle[] {
  const porClave = new Map(previos.map((e) => [claveEvento(e.origen, e.origenId), e]))
  const resultado: EventoGoogle[] = []

  const conservar = (nuevo: EventoGoogle) => {
    const anterior = porClave.get(claveEvento(nuevo.origen, nuevo.origenId))
    if (!anterior) {
      resultado.push(nuevo)
      return
    }
    const cambio =
      anterior.titulo !== nuevo.titulo ||
      anterior.fecha !== nuevo.fecha ||
      anterior.hora !== nuevo.hora ||
      anterior.lugar !== nuevo.lugar ||
      anterior.calendarioId !== nuevo.calendarioId ||
      anterior.recordatorioMinutos !== nuevo.recordatorioMinutos ||
      anterior.abogadoId !== nuevo.abogadoId
    resultado.push(
      cambio
        ? { ...nuevo, ultimaAccion: 'actualizado', actualizadoEn: ahoraISO() }
        : { ...anterior, calendarioId: nuevo.calendarioId },
    )
  }

  for (const a of audiencias) {
    const conexion = conexiones.find((c) => c.abogadoId === a.abogadoId)
    const expediente = expedientes.find((e) => e.id === a.expedienteId)
    if (!conexion || !expediente || !conexionAcepta(conexion, 'audiencia', a.tipo)) continue
    conservar(eventoDeAudiencia(a, expediente, conexion))
  }

  for (const p of plazos) {
    if (p.cumplido) continue
    const conexion = conexiones.find((c) => c.abogadoId === p.responsableId)
    const expediente = expedientes.find((e) => e.id === p.expedienteId)
    if (!conexion || !expediente || !conexionAcepta(conexion, 'plazo')) continue
    conservar(eventoDePlazo(p, expediente, conexion))
  }

  return resultado
}
